// src/components/vehicles/VehicleSettings.js
import React, { useState, useEffect, useContext, useRef } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { AuthContext } from "../../context/AuthContext";
import { api } from "../../utils/api";
import "../../css/Fleet.css";

// --- General Fleet Settings ---
const GeneralSettings = () => {
  const [horizonDays, setHorizonDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const toast = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get("/fleet-settings");
      if (res.data.service_horizon_days) setHorizonDays(parseInt(res.data.service_horizon_days));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const save = async () => {
    try {
      await api.put("/fleet-settings", { key: "service_horizon_days", value: String(horizonDays) });
      toast.current.show({ severity: "success", summary: "Zapisano", detail: `Horyzont ustawiony na ${horizonDays} dni` });
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się zapisać" });
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="fleet-card">
        <div className="fleet-card-header">
          <h3 className="fleet-card-title"><i className="pi pi-calendar" /> Horyzont nadchodzących serwisów</h3>
        </div>
        <div className="fleet-card-body">
          <p style={{ fontSize: "0.9rem", color: "var(--fleet-text-secondary)", marginBottom: "1rem" }}>
            Określ ile dni naprzód mają być wyświetlane nadchodzące terminy serwisów na dashboardzie. Serwisy z terminem ważności przekraczającym ten horyzont nie będą widoczne w tabeli "Nadchodzące terminy serwisów".
          </p>
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
            <div className="fleet-form-field" style={{ flex: "0 0 180px" }}>
              <label>Liczba dni</label>
              <InputNumber value={horizonDays} onValueChange={(e) => setHorizonDays(e.value)} min={1} max={365} suffix=" dni" showButtons disabled={loading} />
            </div>
            <Button label="Zapisz" icon="pi pi-check" className="fleet-btn-primary" size="small" onClick={save} disabled={loading} style={{ height: "fit-content" }} />
          </div>
          <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {[7, 14, 30, 60, 90].map(d => (
              <Button key={d} label={`${d} dni`} size="small" outlined className={horizonDays === d ? "fleet-btn-primary" : ""}
                onClick={() => setHorizonDays(d)} style={{ fontSize: "0.8rem" }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Reusable simple dictionary ---
const SimpleDictionary = ({ endpoint, title }) => {
  const [items, setItems] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const toast = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await api.get(endpoint); setItems(Array.isArray(res.data) ? res.data : []); } catch (err) { console.error(err); }
  };

  const openNew = () => { setEditing(null); setName(""); setDialogVisible(true); };
  const openEdit = (item) => { setEditing(item); setName(item.name); setDialogVisible(true); };

  const save = async () => {
    if (!name.trim()) return;
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, { name });
      else await api.post(endpoint, { name });
      setDialogVisible(false); load();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Błąd", detail: err.response?.data?.message || "Nie udało się zapisać" });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć?")) return;
    try { await api.delete(`${endpoint}/${id}`); load(); }
    catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Błąd", detail: err.response?.data?.message || "Nie udało się usunąć" });
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
        <Button label="Dodaj" icon="pi pi-plus" className="fleet-btn-primary" size="small" onClick={openNew} />
      </div>
      <div className="fleet-card">
        <div className="fleet-card-body--flush fleet-table">
          <DataTable value={items} emptyMessage="Brak danych" size="small">
            <Column field="id" header="ID" style={{ width: "60px" }} />
            <Column field="name" header="Nazwa" />
            {endpoint === "/service-types" && (
              <Column header="Systemowy" body={(row) => row.is_system ? <span className="fleet-tag fleet-tag--info">Systemowy</span> : null} />
            )}
            <Column header="Akcje" body={(row) => (
              <div className="fleet-actions">
                <Button icon="pi pi-pencil" rounded text size="small" severity="warning" onClick={() => openEdit(row)} disabled={endpoint === "/service-types" && row.is_system} tooltip="Edytuj" tooltipOptions={{ position: "top" }} />
                <Button icon="pi pi-trash" rounded text size="small" severity="danger" onClick={() => remove(row.id)} disabled={endpoint === "/service-types" && row.is_system} tooltip="Usuń" tooltipOptions={{ position: "top" }} />
              </div>
            )} style={{ width: "100px" }} />
          </DataTable>
        </div>
      </div>
      <Dialog header={editing ? `Edytuj` : `Dodaj`} visible={dialogVisible} style={{ width: "420px" }} onHide={() => setDialogVisible(false)} className="fleet-dialog"
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}><Button label="Anuluj" className="p-button-text" onClick={() => setDialogVisible(false)} /><Button label="Zapisz" className="fleet-btn-primary" onClick={save} /></div>}>
        <div className="p-fluid">
          <div className="fleet-form-field">
            <label>Nazwa</label>
            <InputText value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

// --- Service Types Manager (z obsługą is_renewable) ---
const ServiceTypesManager = () => {
  const [items, setItems] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [isRenewable, setIsRenewable] = useState(true);
  const toast = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await api.get("/service-types"); setItems(Array.isArray(res.data) ? res.data : []); } catch (err) { console.error(err); }
  };

  const openNew = () => { setEditing(null); setName(""); setIsRenewable(true); setDialogVisible(true); };
  const openEdit = (item) => { setEditing(item); setName(item.name); setIsRenewable(!!item.is_renewable); setDialogVisible(true); };

  const save = async () => {
    if (!editing && !name.trim()) return;
    try {
      if (editing) await api.put(`/service-types/${editing.id}`, { name, is_renewable: isRenewable });
      else await api.post("/service-types", { name, is_renewable: isRenewable });
      setDialogVisible(false); load();
      toast.current?.show({ severity: "success", summary: "Zapisano" });
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Błąd", detail: err.response?.data?.message || "Nie udało się zapisać" });
    }
  };

  const toggleRenewable = async (item) => {
    try {
      await api.put(`/service-types/${item.id}`, { name: item.name, is_renewable: !item.is_renewable });
      load();
    } catch (err) { console.error(err); }
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć?")) return;
    try { await api.delete(`/service-types/${id}`); load(); }
    catch (err) { toast.current?.show({ severity: "error", summary: "Błąd", detail: err.response?.data?.message || "Nie udało się usunąć" }); }
  };

  return (
    <div>
      <Toast ref={toast} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
        <Button label="Dodaj" icon="pi pi-plus" className="fleet-btn-primary" size="small" onClick={openNew} />
      </div>
      <div className="fleet-card">
        <div className="fleet-card-body--flush fleet-table">
          <DataTable value={items} emptyMessage="Brak danych" size="small">
            <Column field="id" header="ID" style={{ width: "60px" }} />
            <Column field="name" header="Nazwa" />
            <Column header="Systemowy" body={(row) => row.is_system ? <span className="fleet-tag fleet-tag--info">Systemowy</span> : null} style={{ width: "100px" }} />
            <Column header="Odnawialny" body={(row) => (
              <InputSwitch checked={!!row.is_renewable} onChange={() => toggleRenewable(row)} tooltip={row.is_renewable ? "Odnawialny (np. OC, AC)" : "Jednorazowy"} tooltipOptions={{ position: "top" }} />
            )} style={{ width: "110px" }} />
            <Column header="Akcje" body={(row) => (
              <div className="fleet-actions">
                <Button icon="pi pi-pencil" rounded text size="small" severity="warning" onClick={() => openEdit(row)} tooltip="Edytuj" tooltipOptions={{ position: "top" }} />
                <Button icon="pi pi-trash" rounded text size="small" severity="danger" onClick={() => remove(row.id)} disabled={row.is_system} tooltip="Usuń" tooltipOptions={{ position: "top" }} />
              </div>
            )} style={{ width: "100px" }} />
          </DataTable>
        </div>
      </div>
      <Dialog header={editing ? "Edytuj typ serwisu" : "Dodaj typ serwisu"} visible={dialogVisible} style={{ width: "460px" }} onHide={() => setDialogVisible(false)} className="fleet-dialog"
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}><Button label="Anuluj" className="p-button-text" onClick={() => setDialogVisible(false)} /><Button label="Zapisz" className="fleet-btn-primary" onClick={save} /></div>}>
        <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div className="fleet-form-field">
            <label>Nazwa</label>
            <InputText value={name} onChange={(e) => setName(e.target.value)} autoFocus disabled={editing?.is_system} />
          </div>
          <div className="fleet-form-field" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <InputSwitch checked={isRenewable} onChange={(e) => setIsRenewable(e.value)} />
            <div>
              <label style={{ fontWeight: 600 }}>Serwis odnawialny</label>
              <p style={{ fontSize: "0.8rem", color: "var(--fleet-text-muted)", margin: 0 }}>
                Serwisy odnawialne (OC, AC, przegląd) mogą być przedłużane. Nie można dodać drugiego wpisu tego samego typu.
              </p>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

// --- Service Type Durations Manager ---
const ServiceDurations = () => {
  const [durations, setDurations] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ serviceTypeId: null, vehicleTypeId: null, durationDays: 365, warningDaysBefore: 30 });
  const toast = useRef(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [dRes, stRes, vtRes] = await Promise.all([
        api.get("/service-type-durations"),
        api.get("/service-types"),
        api.get("/vehicle-types"),
      ]);
      setDurations(Array.isArray(dRes.data) ? dRes.data : []);
      setServiceTypes(Array.isArray(stRes.data) ? stRes.data : []);
      setVehicleTypes(Array.isArray(vtRes.data) ? vtRes.data : []);
    } catch (err) { console.error(err); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ serviceTypeId: null, vehicleTypeId: null, durationDays: 365, warningDaysBefore: 30 });
    setDialogVisible(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ serviceTypeId: item.service_type_id, vehicleTypeId: item.vehicle_type_id, durationDays: item.duration_days, warningDaysBefore: item.warning_days_before });
    setDialogVisible(true);
  };

  const save = async () => {
    if (!form.serviceTypeId || !form.vehicleTypeId || !form.durationDays) {
      toast.current?.show({ severity: "warn", summary: "Uwaga", detail: "Uzupełnij wszystkie pola" }); return;
    }
    try {
      if (editing) await api.put(`/service-type-durations/${editing.id}`, form);
      else await api.post("/service-type-durations", form);
      setDialogVisible(false); loadAll();
      toast.current?.show({ severity: "success", summary: "Zapisano" });
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Błąd", detail: err.response?.data?.message || "Nie udało się zapisać" });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć?")) return;
    try { await api.delete(`/service-type-durations/${id}`); loadAll(); }
    catch (err) { toast.current?.show({ severity: "error", summary: "Błąd" }); }
  };

  const formatDuration = (days) => {
    if (days === 365) return "1 rok";
    if (days === 730) return "2 lata";
    if (days === 1095) return "3 lata";
    if (days % 30 === 0) return `${days / 30} mies.`;
    return `${days} dni`;
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="fleet-card" style={{ marginBottom: "1rem" }}>
        <div className="fleet-card-body" style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.85rem 1.1rem", background: "var(--fleet-info-bg, #eff6ff)", borderRadius: "var(--fleet-radius-sm)" }}>
          <i className="pi pi-info-circle" style={{ fontSize: "1.25rem", color: "var(--fleet-primary)" }} />
          <span style={{ fontSize: "0.9rem", color: "var(--fleet-text-secondary)" }}>
            Zdefiniuj czas trwania serwisów dla każdej kombinacji typu serwisu i typu pojazdu. Te wartości będą używane jako domyślne przy przedłużaniu serwisów.
          </span>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
        <Button label="Dodaj konfigurację" icon="pi pi-plus" className="fleet-btn-primary" size="small" onClick={openNew} />
      </div>
      <div className="fleet-card">
        <div className="fleet-card-body--flush fleet-table">
          <DataTable value={durations} emptyMessage="Brak konfiguracji — dodaj czasy trwania serwisów" size="small" sortField="serviceTypeName" sortOrder={1}>
            <Column field="serviceTypeName" header="Typ serwisu" sortable />
            <Column field="vehicleTypeName" header="Typ pojazdu" sortable />
            <Column header="Czas trwania" body={(row) => (
              <span style={{ fontWeight: 600 }}>{formatDuration(row.duration_days)}</span>
            )} sortable sortField="duration_days" />
            <Column header="Ostrzeżenie przed" body={(row) => `${row.warning_days_before} dni`} />
            <Column header="Akcje" body={(row) => (
              <div className="fleet-actions">
                <Button icon="pi pi-pencil" rounded text size="small" severity="warning" onClick={() => openEdit(row)} tooltip="Edytuj" tooltipOptions={{ position: "top" }} />
                <Button icon="pi pi-trash" rounded text size="small" severity="danger" onClick={() => remove(row.id)} tooltip="Usuń" tooltipOptions={{ position: "top" }} />
              </div>
            )} style={{ width: "100px" }} />
          </DataTable>
        </div>
      </div>
      <Dialog header={editing ? "Edytuj konfigurację" : "Nowa konfiguracja"} visible={dialogVisible} style={{ width: "540px", maxWidth: "95vw" }} onHide={() => setDialogVisible(false)} className="fleet-dialog"
        footer={<div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}><Button label="Anuluj" className="p-button-text" onClick={() => setDialogVisible(false)} /><Button label="Zapisz" className="fleet-btn-primary" onClick={save} /></div>}>
        <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div className="fleet-form-field">
            <label>Typ serwisu *</label>
            <Dropdown value={form.serviceTypeId} options={serviceTypes} onChange={(e) => setForm({ ...form, serviceTypeId: e.value })} optionLabel="name" optionValue="id" placeholder="Wybierz" disabled={!!editing} />
          </div>
          <div className="fleet-form-field">
            <label>Typ pojazdu *</label>
            <Dropdown value={form.vehicleTypeId} options={vehicleTypes} onChange={(e) => setForm({ ...form, vehicleTypeId: e.value })} optionLabel="name" optionValue="id" placeholder="Wybierz" disabled={!!editing} />
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Czas trwania (dni) *</label>
              <InputNumber value={form.durationDays} onValueChange={(e) => setForm({ ...form, durationDays: e.value })} min={1} max={3650} showButtons />
              <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
                {[{ l: "6 mies.", v: 182 }, { l: "1 rok", v: 365 }, { l: "2 lata", v: 730 }, { l: "3 lata", v: 1095 }].map(p => (
                  <Button key={p.v} label={p.l} size="small" outlined onClick={() => setForm({ ...form, durationDays: p.v })}
                    className={form.durationDays === p.v ? "fleet-btn-primary" : ""} style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }} />
                ))}
              </div>
            </div>
            <div className="fleet-form-field">
              <label>Ostrzeżenie (dni przed)</label>
              <InputNumber value={form.warningDaysBefore} onValueChange={(e) => setForm({ ...form, warningDaysBefore: e.value })} min={1} max={365} showButtons />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

// --- Company list (read-only, z tabeli firmy — zarządzanie w panelu admina) ---
const CompanyList = () => {
  const [items, setItems] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await api.get("/vehicle-companies"); setItems(Array.isArray(res.data) ? res.data : []); } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="fleet-card" style={{ marginBottom: "1rem" }}>
        <div className="fleet-card-body" style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.85rem 1.1rem", background: "var(--fleet-info-bg, #eff6ff)", borderRadius: "var(--fleet-radius-sm)" }}>
          <i className="pi pi-info-circle" style={{ fontSize: "1.25rem", color: "var(--fleet-primary)" }} />
          <span style={{ fontSize: "0.9rem", color: "var(--fleet-text-secondary)" }}>
            Firmy są współdzielone z modułem dokumentów WZ. Zarządzanie firmami odbywa się w panelu administracyjnym (Słowniki → Firmy).
          </span>
        </div>
      </div>
      <div className="fleet-card">
        <div className="fleet-card-body--flush fleet-table">
          <DataTable value={items} emptyMessage="Brak firm" size="small">
            <Column field="id" header="ID" style={{ width: "60px" }} />
            <Column field="name" header="Nazwa" />
            <Column field="address" header="Adres" />
            <Column field="nip" header="NIP" />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

// --- Push Notification Settings ---
const NotificationSettings = () => {
  const { user, login } = useContext(AuthContext);
  const [notificationHour, setNotificationHour] = useState("08:00");
  const [subscribed, setSubscribed] = useState(false);
  const [hourLoading, setHourLoading] = useState(true);
  const toast = useRef(null);

  useEffect(() => {
    loadNotificationHour();
    checkSubscription();
  }, []);

  const loadNotificationHour = async () => {
    try {
      const res = await api.get("/notification-settings");
      if (res.data.notificationHour) setNotificationHour(res.data.notificationHour);
    } catch (err) { console.error(err); }
    setHourLoading(false);
  };

  const checkSubscription = async () => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) { const sub = await reg.pushManager.getSubscription(); setSubscribed(!!sub); }
    }
  };

  const subscribe = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        const isInsecure = window.location.protocol !== "https:" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1";
        const detail = isInsecure
          ? "Powiadomienia push wymagają połączenia HTTPS. Skonfiguruj certyfikat SSL na serwerze."
          : "Przeglądarka nie wspiera powiadomień push";
        toast.current.show({ severity: "warn", summary: "Brak wsparcia", detail });
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      const vapidRes = await api.get("/vapid-key");
      const publicKey = vapidRes.data.publicKey;
      if (!publicKey) { toast.current.show({ severity: "warn", summary: "Brak klucza VAPID" }); return; }
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
      const subJSON = sub.toJSON();
      await api.post("/push-subscribe", { endpoint: subJSON.endpoint, keys: subJSON.keys });
      setSubscribed(true);
      toast.current.show({ severity: "success", summary: "Subskrypcja aktywna" });
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: "Nie udało się włączyć powiadomień" });
    } finally {
      checkSubscription();
    }
  };

  const unsubscribe = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) { const sub = await reg.pushManager.getSubscription(); if (sub) { await api.delete("/push-unsubscribe", { data: { endpoint: sub.endpoint } }); await sub.unsubscribe(); } }
      setSubscribed(false);
      toast.current.show({ severity: "success", summary: "Powiadomienia wyłączone" });
    } catch (err) { console.error(err); } finally {
      checkSubscription();
    }
  };

  const saveHour = async () => {
    try {
      await api.put("/notification-settings", { notificationHour });
      // Zaktualizuj user w AuthContext i localStorage
      if (user) {
        const updatedUser = { ...user, notification_hour: notificationHour };
        const storedToken = localStorage.getItem('token');
        if (storedToken) login(updatedUser, storedToken);
      }
      toast.current.show({ severity: "success", summary: "Zapisano", detail: `Godzina powiadomień: ${notificationHour}` });
    } catch (err) {
      console.error(err);
      toast.current.show({ severity: "error", summary: "Błąd", detail: err.response?.data?.message || "Nie udało się zapisać" });
    }
  };

  return (
    <div className="fleet-notification-panel">
      <Toast ref={toast} />
      <div className="fleet-card" style={{ marginBottom: "1.25rem" }}>
        <div className="fleet-card-header">
          <h3 className="fleet-card-title"><i className="pi pi-bell" /> Status powiadomień</h3>
        </div>
        <div className="fleet-card-body">
          <div className={`fleet-notification-status ${subscribed ? "fleet-notification-status--active" : "fleet-notification-status--inactive"}`}>
            <i className={subscribed ? "pi pi-check-circle" : "pi pi-times-circle"} />
            {subscribed ? "Powiadomienia aktywne" : "Powiadomienia wyłączone"}
          </div>
          <div style={{ marginTop: "0.75rem" }}>
            {subscribed
              ? <Button label="Wyłącz powiadomienia" icon="pi pi-bell-slash" severity="danger" size="small" outlined onClick={unsubscribe} />
              : <Button label="Włącz powiadomienia" icon="pi pi-bell" className="fleet-btn-primary" size="small" onClick={subscribe} />
            }
          </div>
        </div>
      </div>
      <div className="fleet-card">
        <div className="fleet-card-header">
          <h3 className="fleet-card-title"><i className="pi pi-clock" /> Godzina powiadomień</h3>
        </div>
        <div className="fleet-card-body">
          <div style={{ display: "flex", gap: "0.65rem", alignItems: "flex-end" }}>
            <div className="fleet-form-field" style={{ flex: "0 0 150px" }}>
              <label>Godzina</label>
              <input type="time" value={notificationHour} onChange={(e) => setNotificationHour(e.target.value)}
                disabled={hourLoading}
                style={{ width: "100%", padding: "0.55rem 0.75rem", border: "1px solid var(--fleet-border)", borderRadius: "var(--fleet-radius-sm)",
                  fontSize: "0.95rem", background: "var(--fleet-surface)", color: "var(--fleet-text)" }} />
            </div>
            <Button label="Zapisz" icon="pi pi-check" className="fleet-btn-primary" size="small" onClick={saveHour} disabled={hourLoading} style={{ height: "fit-content" }} />
          </div>
        </div>
      </div>
    </div>
  );
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// --- Main Settings Page ---
const VehicleSettings = () => {
  const [summary, setSummary] = useState(null);

  useEffect(() => { loadSummary(); }, []);

  const loadSummary = async () => {
    try { const res = await api.get("/settings-summary"); setSummary(res.data); } catch (err) { console.error(err); }
  };

  return (
    <div className="fleet-page fleet-animate-in">
      <div className="fleet-page-header">
        <div>
          <h1><span className="fleet-header-icon">⚙️</span>Ustawienia floty</h1>
          <p className="fleet-page-subtitle">Zarządzaj słownikami, firmami i powiadomieniami</p>
        </div>
      </div>

      {/* Summary stat cards */}
      {summary && (
        <div className="fleet-stats-grid fleet-stats-grid--4">
          <div className="fleet-stat-card fleet-stat-card--primary">
            <div className="fleet-stat-icon fleet-stat-icon--primary"><i className="pi pi-car" /></div>
            <div className="fleet-stat-value fleet-stat-value--primary">{summary.vehicleTypes}</div>
            <div className="fleet-stat-label">Typy pojazdów</div>
          </div>
          <div className="fleet-stat-card fleet-stat-card--success">
            <div className="fleet-stat-icon fleet-stat-icon--success"><i className="pi pi-cog" /></div>
            <div className="fleet-stat-value fleet-stat-value--success">{summary.serviceTypes}</div>
            <div className="fleet-stat-label">Typy serwisów</div>
          </div>
          <div className="fleet-stat-card fleet-stat-card--warning">
            <div className="fleet-stat-icon fleet-stat-icon--warning"><i className="pi pi-wrench" /></div>
            <div className="fleet-stat-value fleet-stat-value--warning">{summary.repairCategories}</div>
            <div className="fleet-stat-label">Kategorie napraw</div>
          </div>
          <div className="fleet-stat-card fleet-stat-card--purple">
            <div className="fleet-stat-icon fleet-stat-icon--purple"><i className="pi pi-building" /></div>
            <div className="fleet-stat-value fleet-stat-value--purple">{summary.companies}</div>
            <div className="fleet-stat-label">Firmy</div>
          </div>
        </div>
      )}

      <div className="fleet-tabs">
        <TabView>
          <TabPanel header="Ustawienia ogólne">
            <GeneralSettings />
          </TabPanel>
          <TabPanel header="Typy pojazdów">
            <SimpleDictionary endpoint="/vehicle-types" title="Typy pojazdów" />
          </TabPanel>
          <TabPanel header="Typy serwisów">
            <ServiceTypesManager />
          </TabPanel>
          <TabPanel header="Czasy trwania serwisów">
            <ServiceDurations />
          </TabPanel>
          <TabPanel header="Kategorie napraw">
            <SimpleDictionary endpoint="/repair-categories" title="Kategorie napraw" />
          </TabPanel>
          <TabPanel header="Firmy">
            <CompanyList />
          </TabPanel>
          <TabPanel header="Powiadomienia">
            <NotificationSettings />
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
};

export default VehicleSettings;
